export const CASE_STUDIES = [
  {
    slug: 'europe-financial-odoo-crm',
    category: 'Odoo ERP',
    industry: 'Digital Banking',
    title: 'Customer Operations Modernization for a Regional Digital Bank',
    subtitle: 'Unified customer profile, payment workflows, dispute handling, and service ticket lifecycle in one platform.',
    metrics: [
      { val: '38%', lbl: 'faster customer response time' },
      { val: '33%', lbl: 'faster dispute resolution' },
      { val: '27%', lbl: 'operational efficiency gain' },
    ],
    headline: 'A single service workspace reduced handoffs and gave agents complete context in every interaction.',
    overview: 'A mid-market digital bank was operating customer care across separate tools for CRM, payments, and support tickets. The fragmented setup slowed response times and made it difficult for agents to track case context end to end.',
    challenge: 'Agents had to switch between systems to understand customer history. Dispute updates depended on manual follow-ups. Ticket ownership was unclear across teams, which increased escalations and first-response delays.',
    approach: [
      'Built a customer 360 dashboard with account, transaction, and interaction timeline.',
      'Implemented guided interaction logging with category and intent tagging.',
      'Automated payment setup, modification, and failed-payment follow-up actions.',
      'Introduced dispute workflows with SLA timers and escalation routing.',
      'Standardized ticket lifecycle states and assignment rules across support queues.',
      'Added card block and reissue workflows with approval checkpoints.',
      'Embedded playbooks and SOP articles for first-contact resolution support.',
    ],
    results: [
      { metric: '38%', label: 'faster customer response time' },
      { metric: '33%', label: 'faster dispute resolution' },
      { metric: '27%', label: 'improvement in operational efficiency' },
      { metric: '22%', label: 'increase in first-contact resolution' },
    ],
    tech: ['Odoo CRM', 'PostgreSQL', 'REST APIs', 'SSO', 'Webhook Orchestration'],
    duration: '4.5 months',
    team: '3 consultants + 1 business analyst',
    quote: 'This rollout gave our support teams one source of truth and materially improved response quality in the first quarter.',
    quoteAuthor: 'Head of Customer Operations, NovaBank Group (name changed)',
  },
  {
    slug: 'multinational-pharma-integration-support',
    category: 'Integration',
    industry: 'Life Sciences',
    title: 'Managed Integration Support for a Global Life Sciences Enterprise',
    subtitle: '24x5 L2/L3 support model with Integration CoE governance for middleware operations.',
    metrics: [
      { val: '29%', lbl: 'improvement in support efficiency' },
      { val: '8 to 5', lbl: 'operations team optimization' },
      { val: '24x5', lbl: 'L2/L3 support coverage' },
    ],
    headline: 'A structured CoE model reduced ticket noise and improved reliability for critical integration flows.',
    overview: 'The client required stable middleware operations for global business processes but faced recurring incidents and inconsistent handoffs between operations and development teams.',
    challenge: 'Incident backlog growth, unclear ownership between support layers, and limited runbook maturity impacted SLA performance for integration-dependent business processes.',
    approach: [
      'Executed phased knowledge transition for 120+ active interfaces.',
      'Established severity-based incident handling and root-cause review cadence.',
      'Created runbooks for top recurring failure patterns and retry procedures.',
      'Introduced CoE governance for design review and release quality checks.',
      'Aligned operations, architecture, and delivery teams through weekly service boards.',
    ],
    results: [
      { metric: '29%', label: 'increase in support efficiency' },
      { metric: '8 to 5', label: 'operations team optimization' },
      { metric: '41%', label: 'reduction in repeat incidents' },
      { metric: '99.2%', label: 'monthly integration availability' },
    ],
    tech: ['MuleSoft Anypoint', 'Oracle ERP', 'ServiceNow', 'Splunk'],
    duration: '6 months',
    team: '1 integration architect + 4 support engineers',
    quote: 'We moved from reactive firefighting to predictable operations with measurable SLA control.',
    quoteAuthor: 'Director of Enterprise Integration, Helix BioSystems (name changed)',
  },
  {
    slug: 'retail-chain-bi-analytics',
    category: 'Analytics',
    industry: 'Retail and E-commerce',
    title: 'Retail BI Program for Multi-Channel Performance Optimization',
    subtitle: 'Unified sales, inventory, and customer dashboards with near real-time performance tracking.',
    metrics: [
      { val: '17%', lbl: 'increase in overall sales' },
      { val: '21%', lbl: 'increase in recurring sales' },
      { val: '29%', lbl: 'increase in gross profitability' },
    ],
    headline: 'Decision cycles shifted from weekly reporting to daily action with trusted KPI visibility.',
    overview: 'A retail chain with online and store channels struggled with fragmented reporting and delayed performance visibility across merchandising, operations, and marketing.',
    challenge: 'Teams relied on multiple spreadsheets and disconnected reports, causing KPI disputes and delayed corrective action on inventory and campaign performance.',
    approach: [
      'Integrated data pipelines from POS, ERP, loyalty, and web commerce sources.',
      'Defined shared KPI glossary and business-owned metric governance.',
      'Built executive, regional, and store-level dashboards with drill-down paths.',
      'Added demand and stockout trend analytics for replenishment planning.',
      'Enabled alerting for margin dips, low-stock risk, and conversion anomalies.',
    ],
    results: [
      { metric: '17%', label: 'increase in overall sales' },
      { metric: '21%', label: 'increase in recurring sales' },
      { metric: '29%', label: 'increase in gross profitability' },
      { metric: '46%', label: 'reduction in manual reporting effort' },
    ],
    tech: ['AWS Redshift', 'Power BI', 'Tableau', 'SAP', 'Salesforce', 'Alteryx'],
    duration: '3.5 months',
    team: '2 data engineers + 1 BI consultant',
    quote: 'Our teams now align on one version of performance, and that changed how quickly we act.',
    quoteAuthor: 'VP of Commercial Analytics, UrbanCart Retail (name changed)',
  },
  {
    slug: 'financial-services-document-automation-ai',
    category: 'AI / Advanced Analytics',
    industry: 'Financial Services',
    title: 'AI Document Automation for Financial Compliance Workflows',
    subtitle: 'Automated extraction, validation, and workflow routing for high-volume financial documents.',
    metrics: [
      { val: '58%', lbl: 'reduction in manual processing effort' },
      { val: '44%', lbl: 'faster validation turnaround' },
      { val: '31%', lbl: 'faster invoice cycle completion' },
    ],
    headline: 'AI-assisted document intelligence improved compliance throughput without adding headcount.',
    overview: 'A financial services provider processed large volumes of invoices and policy documents manually, creating delays in validation and downstream payment workflows.',
    challenge: 'Document checks depended on manual review across multiple teams, resulting in inconsistent extraction quality, longer approval cycles, and avoidable exception queues.',
    approach: [
      'Built secure intake and classification for invoices, statements, and compliance forms.',
      'Implemented OCR plus ML extraction for key entities and tabular data.',
      'Applied rule-based validation and confidence scoring before approval routing.',
      'Added exception queues for low-confidence records and policy breaches.',
      'Integrated validated outputs with downstream finance and reporting systems.',
    ],
    results: [
      { metric: '58%', label: 'reduction in manual document effort' },
      { metric: '44%', label: 'faster validation cycle' },
      { metric: '31%', label: 'faster invoice completion' },
      { metric: '96%', label: 'field-level extraction accuracy (audited sample)' },
    ],
    tech: ['Python', 'AWS Textract', 'AWS Lambda', 'React', 'MongoDB', 'REST APIs'],
    duration: '5 months',
    team: '1 AI engineer + 2 full-stack developers',
    quote: 'We cut review time dramatically while improving audit confidence across document-heavy workflows.',
    quoteAuthor: 'Head of Process Excellence, Meridian Financial Services (name changed)',
  },
];

const CASES = CASE_STUDIES.map(({ slug, category, industry, title, subtitle, metrics, tech, duration }) => ({
  slug,
  category,
  industry,
  title,
  subtitle,
  metrics,
  tech,
  duration,
}));

export default CASES;
